import type { BuildAndBuyDispatchType } from "../../../redux/store";
import { useBnbSelector } from "../../../redux/store";
import type { ExtrasCategoryToFilterType, ExtraType } from "../../../../../../common-deprecated/types/CommonTypes";
import {
    applyExtrasFilter,
    setAvailableOnlineToFilter,
    setSearchString,
    setTempCategoriesToFilter,
    setTempRootCategoryToFilter,
    toggleExtrasFilter,
} from "../../../redux/actions/ExtrasFilterActions";
import CarDataConsolidator from "../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { trackExtrasApplyFilter, trackExtrasOpenFilter } from "../../../utils/tracking";
import { getBnbLabel } from "../../../utils/labels";
import type { CommonSettingsType } from "../../../../../../common-deprecated/settings/fetchCommonSettings";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

const getCategoryIconType = (category?: string): string => {
    switch (category) {
        case "EXT/ExteriorStyling":
            return "star-alt"; //"Styling"
        case "EXT/ExteriorComfort":
            return "heart"; //"Comfort"
        case "EXT/ExteriorTransport":
            return "transport"; //"Transport"
        case "EXT/ExteriorProtection":
            return "insurance-alt"; //"Protection"
        case "EXT/ExteriorPerformance":
            return "consumptions"; //  "Performance"
        case "EXT/ExteriorSafety":
            return "cone"; //"Safety"
        case "INT/Multimedia":
            return "tablet"; //"Multimedia"
        case "INT/InteriorStyling":
            return "star-alt"; //"Styling"
        case "INT/InteriorComfort":
            return "heart"; //"Comfort"
        case "INT/InteriorTransport":
            return "transport"; //"Transport"
        case "INT/InteriorProtection":
            return "insurance-alt"; //"Protection"
        case "INT/InteriorPerformance":
            return "consumptions"; //  "Performance"
        case "INT/InteriorSafety":
            return "cone"; //"Safety"
        default:
            return "";
    }
};

export const filterExtrasForFilter = (
    extras: ExtraType[],
    rootCategoryToFilter: ExtrasCategoryToFilterType | null,
    searchString: string,
    wheelExtras: ExtraType[],
    filterCarOptions: ExtraType[],
    settings: CommonSettingsType,
): ExtrasCategoryToFilterType[] => {
    let allItems = extras
        .filter((extra) => !CarDataConsolidator.hideExtra(extra, settings))
        .map((extra) => ({
            rootCategoryName: extra.category.root && extra.category.root.name,
            rootCategoryId: extra.category.root && extra.category.root.id,
            categoryName: extra.category.name,
            categoryCode: extra.category.code,
            extraName: extra.name,
            extraId: extra.id,
            extraShortId: extra.shortId,
            id: extra.category.id,
        }));

    // Do not show wheel extras in the extra container. These should be resolved by include/exclude rules.
    allItems = allItems.filter((item) => !wheelExtras.find((wheelExtra) => wheelExtra.id === item.extraId));

    // filterCarOptions shouldn't be shown in the extra's list.
    if (filterCarOptions) {
        allItems = allItems.filter(
            (item) => !filterCarOptions.map((option) => option.shortId).includes(item.extraShortId),
        );
    }
    const uniqueItemNames = Array.from(new Set(allItems.map((item) => item.categoryName)));
    return uniqueItemNames
        .map((uniqueItemName) => {
            return {
                name: uniqueItemName,
                isSelected: false,
                id: allItems.filter((item) => item.categoryName === uniqueItemName)[0].id,
                code: allItems.filter((item) => item.categoryName === uniqueItemName)[0].categoryCode,

                count: rootCategoryToFilter
                    ? allItems.filter(
                          (item) =>
                              item.categoryName === uniqueItemName &&
                              rootCategoryToFilter &&
                              rootCategoryToFilter.id === item.rootCategoryId &&
                              item.extraName.toLowerCase().indexOf(searchString.toLowerCase()) !== -1,
                      ).length
                    : allItems.filter(
                          (item) =>
                              item.categoryName === uniqueItemName &&
                              item.extraName.toLowerCase().indexOf(searchString.toLowerCase()) !== -1,
                      ).length,
            };
            // Only show filters we know and have icons for
        })
        .filter((category) => !!getCategoryIconType(category.code));
};

export type UseExtrasCategoryFilterType = {
    categories: ExtrasCategoryToFilterType[];
    rootCategories: ExtrasCategoryToFilterType[];
    onSetTempRootCategoryToFilter: (rootCategory: ExtrasCategoryToFilterType | null) => void;
    onSetTempCategoriesToFilter: (categoriesToFilter: ExtrasCategoryToFilterType[]) => void;
    onSetSearchString: (searchString: string) => void;
    onSetAvailableOnlineToFilter: (availableOnlineToFilter: boolean) => void;
    onApplyExtrasFilter: () => void;
    onToggleExtrasFilter: () => void;
};
const useExtraCategoryFilter = (): UseExtrasCategoryFilterType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const items = useBnbSelector((state) => state.items);
    const extrasFilter = useBnbSelector((state) => state.extrasFilter);
    const settings = useBnbSelector((state) => state.commonSettings);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);

    const { extras, wheelExtras, filterCarOptions } = items;
    const { tempCategoriesToFilter, tempRootCategoryToFilter, searchString, showExtrasFilter } = extrasFilter;

    const categories = filterExtrasForFilter(
        extras,
        tempRootCategoryToFilter,
        searchString,
        wheelExtras,
        filterCarOptions,
        settings,
    );
    const tempRootCategories = extras.map((extra) => {
        return {
            id: extra.category.root && extra.category.root.id,
            name: extra.category.root && extra.category.root.name,
            isSelected:
                tempRootCategoryToFilter &&
                extra.category.root &&
                tempRootCategoryToFilter.id === extra.category.root.id,
        };
    });

    const rootCategories = tempRootCategories.reduce((unique: ExtrasCategoryToFilterType[], o) => {
        if (!unique.some((obj) => obj.id === o.id && obj.name === o.name)) {
            unique.push(o);
        }
        return unique;
    }, []);

    rootCategories.push({
        id: "allExtras",
        name: getBnbLabel({ commonSettings: settings, bnbSettings }, "allExtrasDropdown"),
        isSelected: !rootCategories.find((x) => x.isSelected),
    });
    categories.forEach((c) => {
        c.isSelected = tempCategoriesToFilter.map((f) => f.id).includes(c.id);
    });

    return {
        categories,
        rootCategories,
        onSetSearchString: (newSearchString) => {
            dispatch(setSearchString(newSearchString));
        },
        onSetTempRootCategoryToFilter: (rootCategory) => {
            dispatch(setTempRootCategoryToFilter(rootCategory));
        },
        onSetTempCategoriesToFilter: (newCategoriesToFilter) => {
            dispatch(setTempCategoriesToFilter(newCategoriesToFilter));
        },
        onSetAvailableOnlineToFilter: (newAvailableOnlineToFilter) => {
            dispatch(setAvailableOnlineToFilter(newAvailableOnlineToFilter));
        },
        onApplyExtrasFilter: () => {
            trackExtrasApplyFilter();
            dispatch(applyExtrasFilter());
        },
        onToggleExtrasFilter: () => {
            if (!showExtrasFilter) {
                trackExtrasOpenFilter();
            }
            dispatch(toggleExtrasFilter());
        },
    };
};

export default useExtraCategoryFilter;
